// backgroundRecipeGen — run the slow "final pass" recipe generation
// detached from any screen, writing status into a pending Recipe-Book
// stub the user can watch.
//
// Why this exists: the final pass is a single Sonnet streaming call
// with no server-side fallback. When the upstream stalls (a dropped
// connection, an overloaded API) the foreground flow has no timeout and
// the user is pinned to a spinner forever. Instead we mint a pending
// user_recipes row, drop the user into their Recipe Book on it, and run
// the generation here — owned by App, so it survives the AIRecipe /
// CreateMenu overlay tearing down. When it resolves we flip the stub to
// the finished recipe; when it fails (or times out) we flip it to a
// "failed → Retry" state. The user is never stuck on a screen.
//
// Status lives in recipe._gen ("pending" | "failed"); a ready recipe
// simply has no _gen flag (a normal recipe). We DON'T store the
// generation payload in the row — it would ride along on every read of
// that recipe (egress) — so the retry inputs are stashed in
// localStorage, which is local + free. If the cache is gone (different
// device, cleared storage) Retry degrades to "regenerate from Create".

import { supabase } from "./supabase";
import { generateRecipeStreaming } from "./generateRecipe";
import { finalizeGeneratedRecipe } from "./finalizeGeneratedRecipe";
import { recordGeneration } from "./generationLedger";

// Hard ceiling on a single attempt. Sonnet final at 4k tokens lands in
// 30-90s on a healthy connection; past this we assume the stream is
// wedged and surface a retryable failure rather than hanging.
const TIMEOUT_MS = 150_000;

const cacheKey = (stubId) => `bgrecipe:${stubId}`;

function stashRetryInputs(stubId, inputs) {
  try {
    localStorage.setItem(cacheKey(stubId), JSON.stringify(inputs));
  } catch (_) { /* storage full / unavailable — retry degrades, non-fatal */ }
}

function readRetryInputs(stubId) {
  try {
    const raw = localStorage.getItem(cacheKey(stubId));
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

function clearRetryInputs(stubId) {
  try { localStorage.removeItem(cacheKey(stubId)); } catch (_) { /* noop */ }
}

// Raw status write — bypasses the useUserRecipes / useRecipeBook hooks
// on purpose. Those version-snapshot the prior recipe on every content
// update, which would archive the empty stub as a junk "past version."
// A raw update by id propagates to both hooks via their realtime
// subscriptions, so the open Recipe Book flips live.
async function writeStub(stubId, recipe) {
  const { error } = await supabase
    .from("user_recipes")
    .update({ recipe })
    .eq("id", stubId);
  if (error) {
    // eslint-disable-next-line no-console
    console.error("[backgroundRecipeGen] stub write failed:", error.message);
  }
}

// Vessel hint for the goal-image prompt — mirrors inferVesselHint in
// useUserRecipes (kept small + local so this lib stays standalone).
function vesselHint(recipe) {
  const haystack = [
    Array.isArray(recipe?.ingredients)
      ? recipe.ingredients.map((i) => (typeof i === "string" ? i : (i?.item || i?.name || ""))).join(" ")
      : "",
    Array.isArray(recipe?.steps)
      ? recipe.steps.map((s) => `${s?.title || ""} ${s?.instruction || ""}`).join(" ")
      : "",
    recipe?.title || "",
  ].join(" ").toLowerCase();
  if (/\b(cast[\s-]?iron|skillet|pan[\s-]?fry|pan[\s-]?sear|frittata)\b/.test(haystack)) return "skillet";
  if (/\b(risotto|soup|stew|chili|congee|porridge|ramen|pho|chowder|gumbo|bowl)\b/.test(haystack)) return "bowl";
  return "plate";
}

// Goal image for the finished recipe — AWAITED (not fire-and-forget) so a
// failure becomes a visible build issue instead of a silently missing
// photo. Called WITHOUT recipeId so the edge function just returns the URL
// (we write it ourselves in the single readyRecipe write — no race). On
// success returns { url }; on failure returns { error } for the caller to
// surface to the user.
async function generateGoalImage(recipe) {
  if (!recipe?.title) return { url: null, error: null };
  try {
    const { data, error } = await supabase.functions.invoke("generate-recipe-image", {
      body: { recipeName: recipe.title, vessel: vesselHint(recipe) },
    });
    if (error) {
      let detail = error.message || "image generation failed";
      try {
        const b = await error.context?.text?.();
        if (b) {
          detail = b.slice(0, 200);
          // Metered denial (docs/AI_TOKENS.md) — surface the human
          // sentence, not truncated raw JSON, on the recipe page's
          // Photo build issue.
          try {
            const parsed = JSON.parse(b);
            if (parsed?.error === "ai_tokens_exhausted") {
              detail = parsed.message || "Weekly AI allowance is used up — it resets Monday.";
            }
          } catch (_) { /* not JSON — keep the sliced text */ }
        }
      } catch (_) { /* noop */ }
      return { url: null, error: detail };
    }
    if (!data?.goalImageURL) return { url: null, error: "no image returned" };
    return { url: data.goalImageURL, error: null };
  } catch (e) {
    return { url: null, error: String(e?.message || e).slice(0, 200) };
  }
}

/**
 * Retry a single failed part of an already-built recipe (e.g. the photo)
 * without regenerating the whole thing. Reads the row, re-runs the part,
 * writes back with that issue cleared. Used by the Recipe Book's per-issue
 * Retry button.
 * @returns {Promise<boolean>} true if the part succeeded
 */
export async function retryRecipePart(recipeId, part) {
  if (!recipeId || !part) return false;
  const { data: row } = await supabase
    .from("user_recipes").select("recipe").eq("id", recipeId).maybeSingle();
  const recipe = row?.recipe;
  if (!recipe) return false;
  const issues = (Array.isArray(recipe._buildIssues) ? recipe._buildIssues : [])
    .filter((i) => i?.retry !== part);
  let patch = {};
  let ok = false;
  if (part === "image") {
    const { url, error } = await generateGoalImage(recipe);
    if (url) { patch = { goalImageURL: url }; ok = true; }
    else issues.push({ part: "Photo", error: error || "image generation failed", retry: "image" });
  }
  const next = { ...recipe, ...patch };
  if (issues.length) next._buildIssues = issues; else delete next._buildIssues;
  await writeStub(recipeId, next);
  return ok;
}

function failedStub(stubMeta, message) {
  return {
    // stubMeta.carry — opaque fields the caller needs to SURVIVE every
    // full-jsonb rewrite of the stub row (e.g. the side-dish flow's
    // pairedWith back-ref). Spread first so status fields always win.
    ...(stubMeta?.carry || {}),
    title: stubMeta?.title || "Recipe",
    subtitle: stubMeta?.subtitle || "",
    emoji: stubMeta?.emoji || "🍳",
    ingredients: [],
    steps: [],
    _gen: "failed",
    _genError: String(message || "Generation failed").slice(0, 280),
  };
}

/**
 * Run a background generation against an already-created pending stub.
 * Fire-and-forget: App awaits nothing here. On success the stub row
 * becomes the finished recipe; on failure/timeout it becomes a failed
 * stub the user can Retry.
 *
 * @param {object} args
 * @param {string} args.stubId          — user_recipes.id of the pending stub
 * @param {object} args.payload         — generateRecipeStreaming args (pantry, prefs, …)
 * @param {object} args.finalizeInputs  — { locked, priority, isDreamMode }
 * @param {object} args.stubMeta        — { title, subtitle, emoji } for the failed state
 */
export async function runBackgroundRecipeGen({ stubId, payload, finalizeInputs, stubMeta }) {
  if (!stubId) return;
  // Persist the retry inputs up front so a Retry works even if this
  // attempt is interrupted (tab closed mid-flight, etc.).
  stashRetryInputs(stubId, { payload, finalizeInputs, stubMeta });

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const { recipe: drafted } = await generateRecipeStreaming({
      ...payload,
      stubId,                     // edge fn persists the finished recipe to this
                                  // row under waitUntil, so it lands even if this
                                  // client fetch is aborted (app backgrounded).
      onChunk: () => {},          // no UI to stream into in the background
      signal: controller.signal,
    });
    if (!drafted) throw new Error("Generation returned no recipe");
    const finalRecipe = await finalizeGeneratedRecipe(drafted, finalizeInputs || {});
    // Generation ledger (A5) — this background handoff is the DEFAULT
    // production path (AIRecipe's inline finalize is the no-handler
    // fallback), so the ledger write must live here or it never fires.
    // userId + frame ride finalizeInputs; both are JSON-serializable
    // so they survive the localStorage retry stash. Fire-and-forget.
    recordGeneration({
      userId: finalizeInputs?.userId || null,
      recipe: finalRecipe,
      frame: finalizeInputs?.frame || null,
      getInfo: null,   // no ingredientInfo context off-screen; taste_tags fall back to []
    });
    // Collect per-part failures (reheat from finalize + the goal image
    // below) so a broken piece is SURFACED to the user, never silent —
    // the recipe still lands with everything that DID build.
    const buildIssues = Array.isArray(finalRecipe._buildIssues) ? [...finalRecipe._buildIssues] : [];
    let goalImageURL = null;
    if (!finalRecipe.goalImageURL) {
      const img = await generateGoalImage(finalRecipe);
      if (img.url) goalImageURL = img.url;
      else if (img.error) buildIssues.push({ part: "Photo", error: img.error, retry: "image" });
    }
    // Ready: ONE write with the recipe + image + any build issues. No
    // _gen flag (a ready recipe). Carry the stub's emoji if the model
    // didn't author one.
    const readyRecipe = {
      ...finalRecipe,
      // Re-merge carried stub fields (pairedWith etc.) — the model's
      // draft doesn't know about them and this write is wholesale.
      ...(stubMeta?.carry || {}),
      emoji: finalRecipe.emoji || stubMeta?.emoji || "🍳",
      ...(goalImageURL ? { goalImageURL } : {}),
    };
    if (buildIssues.length) readyRecipe._buildIssues = buildIssues;
    else delete readyRecipe._buildIssues;
    await writeStub(stubId, readyRecipe);
    clearRetryInputs(stubId);
  } catch (e) {
    // Before declaring failure, check whether the SERVER already landed
    // the recipe. When the user backgrounds the app, THIS client fetch
    // aborts — but the edge function keeps generating under
    // EdgeRuntime.waitUntil and writes the finished recipe straight to
    // this stub row. Writing a "failed" state here would clobber a
    // recipe that actually succeeded. So re-read first: if the row now
    // holds a real recipe (steps present, not itself a failed stub),
    // leave it be — the server won.
    try {
      const { data } = await supabase
        .from("user_recipes").select("recipe").eq("id", stubId).maybeSingle();
      const landed = data?.recipe;
      if (landed && Array.isArray(landed.steps) && landed.steps.length > 0
          && landed._gen !== "failed" && landed._gen !== "pending") {
        clearRetryInputs(stubId);
        return;   // server-persisted recipe wins; don't overwrite with failure.
      }
    } catch (_) { /* fall through to the failed write */ }
    const aborted = e?.name === "AbortError" || controller.signal.aborted;
    const message = aborted
      ? "The connection dropped before this finished. Tap Retry."
      : (e?.message || "Generation failed. Tap Retry.");
    await writeStub(stubId, failedStub(stubMeta, message));
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Re-run a failed (or stuck) generation against the same stub row,
 * reading the inputs stashed at first-run time. Flips the row back to
 * pending immediately, then re-enters the normal run.
 *
 * @returns {Promise<boolean>} true if a retry was started, false if the
 *          cached inputs are gone (caller should send the user to Create).
 */
export async function retryBackgroundRecipeGen(stubId) {
  if (!stubId) return false;
  const inputs = readRetryInputs(stubId);
  if (!inputs?.payload) return false;
  const { payload, finalizeInputs, stubMeta } = inputs;
  // Back to pending so the Recipe Book shows the spinner again.
  await writeStub(stubId, {
    ...(stubMeta?.carry || {}),
    title: stubMeta?.title || "Recipe",
    subtitle: stubMeta?.subtitle || "",
    emoji: stubMeta?.emoji || "🍳",
    ingredients: [],
    steps: [],
    _gen: "pending",
    _genStartedAt: new Date().toISOString(),
  });
  runBackgroundRecipeGen({ stubId, payload, finalizeInputs, stubMeta });
  return true;
}
